import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, scrollToSection } from "../i18n";
import { bookingSearchUrl, IMAGES, trackEvent } from "../data";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [search, setSearch] = useState({ where: "", checkin: "", checkout: "", guests: "" });
  const setField = (key) => (e) => setSearch((s) => ({ ...s, [key]: e.target.value }));

  const submitSearch = (e) => {
    e.preventDefault();
    if (search.checkin && search.checkout && search.checkout <= search.checkin) {
      toast.error(lang === "en" ? "Check-out must be after check-in." : "Il check-out deve essere dopo il check-in.");
      return;
    }
    trackEvent("availability_search", {
      destination: search.where.trim() || undefined,
      checkin: search.checkin || undefined,
      checkout: search.checkout || undefined,
      guests: search.guests || undefined,
    });
    window.location.assign(bookingSearchUrl({ ...search, lang }));
  };
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fgY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative flex min-h-[100svh] flex-col overflow-hidden bg-navy">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src={IMAGES.hero}
          alt="Costa dell'Ogliastra, Sardegna"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1.05 }}
          transition={{ duration: 2.6, ease: EASE }}
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/25 to-navy/85" />
      <div className="absolute inset-0 bg-gradient-to-tr from-sky/25 via-transparent to-sky/10 mix-blend-overlay" />

      <motion.div
        style={{ y: fgY, opacity: fade }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 pb-40 pt-36 sm:px-8 lg:pb-48"
      >
        <motion.p
          data-testid="hero-kicker"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
          className="kicker text-sky"
        >
          <span className="h-px w-10 bg-sky" />
          {t.hero.kicker}
        </motion.p>

        <h1 data-testid="hero-headline" className="mt-7 font-display text-[clamp(2.9rem,7.5vw,6.4rem)] font-semibold leading-[1.02] tracking-tight text-white">
          {t.hero.lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-sand" : ""}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.65 + i * 0.16, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-6 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => navigate("/strutture")}
            className="btn-primary"
          >
            {t.hero.ctaPrimary}
          </button>
          <button data-testid="hero-cta-secondary" onClick={() => scrollToSection("#metodo-home")} className="btn-secondary">
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.form
          data-testid="hero-search-bar"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.5, ease: EASE }}
          onSubmit={submitSearch}
          className="mt-14 flex w-full max-w-3xl flex-col gap-2 rounded-[2rem] bg-white/15 p-2 ring-1 ring-white/25 backdrop-blur-xl sm:flex-row sm:items-stretch sm:gap-0 sm:rounded-full"
        >
          <label className="flex min-w-0 flex-col justify-center px-5 py-3 sm:flex-[1.3]">
            <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-sky">{t.hero.search.where}</span>
            <input
              data-testid="hero-search-where"
              value={search.where}
              onChange={setField("where")}
              placeholder={t.hero.search.wherePh}
              className="w-full min-w-0 bg-transparent text-sm font-semibold text-white placeholder-white/50 focus:outline-none"
            />
          </label>
          <label className="flex min-w-0 flex-col justify-center border-white/10 px-5 py-3 sm:flex-1 sm:border-l">
            <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-sky">{t.hero.search.checkin}</span>
            <input data-testid="hero-search-checkin" type="date" value={search.checkin} onChange={setField("checkin")} className="w-full min-w-0 bg-transparent text-sm font-semibold text-white/90 [color-scheme:dark] focus:outline-none" />
          </label>
          <label className="flex min-w-0 flex-col justify-center border-white/10 px-5 py-3 sm:flex-1 sm:border-l">
            <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-sky">{t.hero.search.checkout}</span>
            <input data-testid="hero-search-checkout" type="date" value={search.checkout} onChange={setField("checkout")} className="w-full min-w-0 bg-transparent text-sm font-semibold text-white/90 [color-scheme:dark] focus:outline-none" />
          </label>
          <label className="flex min-w-0 flex-col justify-center border-white/10 px-5 py-3 sm:flex-1 sm:border-l">
            <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-sky">{t.hero.search.guests}</span>
            <input
              data-testid="hero-search-guests"
              type="number"
              min="1"
              max="16"
              value={search.guests}
              onChange={setField("guests")}
              placeholder={t.hero.search.guestsPh}
              className="w-full min-w-0 bg-transparent text-sm font-semibold text-white placeholder-white/50 focus:outline-none"
            />
          </label>
          <button
            data-testid="hero-search-submit"
            type="submit"
            className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-sky px-7 py-3.5 text-sm font-bold text-navy transition-[background-color,color,transform] duration-300 hover:bg-navy hover:text-white hover:scale-[1.03]"
          >
            <Search className="h-4 w-4" />
            {t.hero.search.button}
          </button>
        </motion.form>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("#case")}
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-white/70 transition-colors hover:text-white"
      >
        <span className="text-[10px] font-extrabold uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ChevronDown className="h-5 w-5" />
        </motion.span>
      </motion.button>

      <div className="pointer-events-none absolute bottom-6 right-6 z-10 hidden items-center gap-2 text-white/60 lg:flex">
        <MapPin className="h-3.5 w-3.5 text-sky" />
        <span className="text-[11px] font-bold uppercase tracking-[0.25em]">39.9°N · 9.6°E</span>
      </div>
    </section>
  );
}
